import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Tabs, Card, Select, Empty } from "antd";
import { selectAuthInfo } from "../Auth/authSelectors";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useAttendanceChartData } from "./useAttendanceChartData";

const COLORS = ["#4caf50", "#ff9800", "#03a9f4", "#e91e63", "#9254de", "#13c2c2"];

export default function AttendanceCharts({
  analyticsReport = [],
  trendReport = [],
  employees = [],
}) {
  const { barData, lineData, pieData } = useAttendanceChartData(
    analyticsReport,
    trendReport
  );

  const authInfo = useSelector(selectAuthInfo);
  const casualOrAnnualLabel =
    authInfo?.country === "India" ? "Casual Leave" : "Annual Leave";

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const hasLongEmployeeName = barData.some(
    (item) => (item.employee || "").length > 18,
  );

  return (
    <Card style={{ borderRadius: 12 }}>
      <Tabs
        defaultActiveKey="bar"
        items={[
          {
            key: "bar",
            label: "Summary (Stacked Bar)",
            children: barData.length ? (
              // <ResponsiveContainer width="100%" height={360}>
              //   <BarChart data={barData} barSize={32}>
              //     <XAxis dataKey="employee" />
              //     <YAxis allowDecimals={false} domain={[0, "dataMax + 1"]} />
              //     <Tooltip />
              //     <Legend layout="horizontal" verticalAlign="bottom" align="center" />
              //     <Bar dataKey="present" stackId="a" fill="#4caf50" name="Present" />
              //     <Bar dataKey="sick" stackId="a" fill="#ff9800" name="Sick Leave" />
              //     <Bar dataKey="casual" stackId="a" fill="#03a9f4" name="Casual Leave" />
              //     <Bar dataKey="absent" stackId="a" fill="#e91e63" name="Absent" />
              //   </BarChart>
              // </ResponsiveContainer>
              <div
                style={{
                  height: 500,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(500, barData.length * 45)}
                >
                  <BarChart
                    data={barData}
                    layout="vertical"
                    barSize={24}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 20,
                      bottom: 50,
                    }}
                  >
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      domain={[0, "dataMax + 1"]}
                    />

                    <YAxis
                      type="category"
                      dataKey="employee"
                      width={220}
                      tick={{
                        fontSize: 12,
                        angle: hasLongEmployeeName ? -25 : 0,
                        textAnchor: hasLongEmployeeName ? "end" : "middle",
                      }}
                    />

                    <Tooltip />

                    <Legend verticalAlign="bottom" height={30} />

                    <Bar
                      dataKey="present"
                      stackId="a"
                      fill="#4caf50"
                      name="Present"
                    />

                    <Bar
                      dataKey="sick"
                      stackId="a"
                      fill="#ff9800"
                      name="Sick Leave"
                    />

                    <Bar
                      dataKey="casual"
                      stackId="a"
                      fill="#03a9f4"
                      name={casualOrAnnualLabel}
                    />

                    <Bar
                      dataKey="absent"
                      stackId="a"
                      fill="#e91e63"
                      name="Absent"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Empty />
            ),
          },
          {
            key: "line",
            label: "Daily Trend (Line Chart)",
            children: lineData.length ? (
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={lineData}>
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="present"
                    stroke="#4caf50"
                    strokeWidth={2}
                    name="Present"
                  />
                  <Line
                    type="monotone"
                    dataKey="leave"
                    stroke="#ff9800"
                    strokeWidth={2}
                    name="Leave"
                  />
                  <Line
                    type="monotone"
                    dataKey="absent"
                    stroke="#e91e63"
                    strokeWidth={2}
                    name="Absent"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No daily attendance trend data available" />
            ),
          },
          {
            key: "pie",
            label: "Leave Breakdown (Pie Chart)",
            children: (
              <>
                <Select
                  placeholder="Select Employee"
                  style={{ width: 260, marginBottom: 16 }}
                  onChange={setSelectedEmployee}
                  options={analyticsReport.map((emp) => ({
                    value: emp.employee_id,
                    label: emp.employee_name,
                  }))}
                />

                {selectedEmployee && pieData[selectedEmployee] ? (
                  <ResponsiveContainer width="100%" height={360}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Sick Leave",
                            value: pieData[selectedEmployee].sick,
                          },
                          {
                            name: casualOrAnnualLabel,
                            value: pieData[selectedEmployee].casual,
                          },
                          {
                            name: "Absent",
                            value: pieData[selectedEmployee].absent,
                          },
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        dataKey="value"
                        label
                      >
                        {COLORS.map((color, index) => (
                          <Cell key={index} fill={color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="Select an employee" />
                )}
              </>
            ),
          },
        ]}
      />
    </Card>
  );
}